/**
 * halloween.js
 * ---------------------------------------------------------------------------
 * Erzeugt die Fledermäuse und Geister im Hintergrund und lässt sie umherziehen.
 * Nähert sich der Mauszeiger auf weniger als CONFIG.fleeRadius Pixel, stieben
 * sie auseinander und beruhigen sich danach wieder auf ihr Reisetempo.
 *
 * Zusätzlich verschiebt eine leichte Parallaxe Mond und Silhouette.
 * Kein Klick-Verhalten, keine Abhängigkeiten, kein Build.
 *
 * Startet nicht von selbst — einmal aufrufen:
 *
 *   import { initHalloween } from "./halloween.js";
 *   initHalloween();
 *
 * Aktiviert wird jedes Element mit der Klasse .hw-scene. Wird mindestens eine
 * gefunden, bekommt der body zusätzlich die Klasse .is-halloween — damit kann
 * die Seite ihr eigenes Styling umschalten, ohne von der Szene zu wissen.
 */
export function halloween() {
  "use strict";

  var CONFIG = {
    bats:        7,      // Anzahl Fledermäuse (Desktop, mobil halbiert)
    ghosts:      5,      // Anzahl Geister
    fleeRadius:  170,    // ab dieser Distanz zum Zeiger weichen sie aus (px)
    fleeForce:   1.1,    // Heftigkeit des Ausweichens
    maxSpeed:    3.6,    // Fluchttempo (px pro Frame bei 60 fps)
    batSpeed:    0.85,   // Reisetempo Fledermaus
    ghostSpeed:  0.4,    // Reisetempo Geist
    wander:      0.05,   // Stärke der zufälligen Richtungswechsel
    calmDown:    0.012,  // wie schnell der Schreck abklingt
    margin:      70,     // ab diesem Randabstand drehen sie ein
    batSize:     [16, 32],
    ghostSize:   [20, 36]
  };

  var BAT =
      '<span class="hw-critter__body">' +
      '<i class="hw-wing hw-wing--l"></i>' +
      '<i class="hw-bat-body"></i>' +
      '<i class="hw-wing hw-wing--r"></i>' +
      '</span>';

  var GHOST = '<span class="hw-critter__body"><i></i><i></i></span>';

  function reducedMotion() {
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  }

  function rand(min, max) { return min + Math.random() * (max - min); }

  function create(scene) {
    var layer = scene.querySelector(".hw-critters");
    if (!layer) return;

    var W = window.innerWidth;
    var H = window.innerHeight;
    var small = W < 700;
    var critters = [];
    var pointer = { x: -9999, y: -9999, active: false };
    var running = true;
    var last = 0;

    function spawn(kind, count) {
      var range = kind === "bat" ? CONFIG.batSize : CONFIG.ghostSize;

      for (var i = 0; i < count; i++) {
        var size = rand(range[0], range[1]);
        var el = document.createElement("span");
        el.className = "hw-critter hw-critter--" + kind;
        el.innerHTML = kind === "bat" ? BAT : GHOST;
        el.style.setProperty("--hw-size", size.toFixed(1) + "px");
        el.style.opacity = kind === "ghost" ? rand(0.5, 0.85).toFixed(2) : "1";
        layer.appendChild(el);

        var dir = rand(0, Math.PI * 2);
        var base = (kind === "bat" ? CONFIG.batSpeed : CONFIG.ghostSpeed) * rand(0.75, 1.3);

        critters.push({
          el: el,
          kind: kind,
          w: size,
          h: kind === "bat" ? size * 0.5 : size / 0.82,
          x: rand(0, W),
          y: rand(0, H * (kind === "bat" ? 0.7 : 0.85)),
          vx: Math.cos(dir) * base,
          vy: Math.sin(dir) * base,
          base: base,
          flee: 0,        // 1 = gerade aufgeschreckt, klingt auf 0 ab
          face: 1,
          fleeing: false
        });
      }
    }

    spawn("bat", small ? Math.ceil(CONFIG.bats / 2) : CONFIG.bats);
    spawn("ghost", small ? Math.ceil(CONFIG.ghosts / 2) : CONFIG.ghosts);

    function step(now) {
      if (!running) return;
      var dt = last ? Math.min((now - last) / 16.667, 3) : 1;
      last = now;

      for (var i = 0; i < critters.length; i++) {
        var c = critters[i];

        // 1) Umherstreifen: kleine Zufallsimpulse auf die Richtung
        c.vx += rand(-1, 1) * CONFIG.wander * dt;
        c.vy += rand(-1, 1) * CONFIG.wander * dt;

        // 2) Ausweichen: Stoß entlang der Achse Zeiger -> Kreatur
        if (pointer.active) {
          var dx = c.x - pointer.x;
          var dy = c.y - pointer.y;
          var d = Math.sqrt(dx * dx + dy * dy);

          if (d < CONFIG.fleeRadius) {
            var push = 1 - d / CONFIG.fleeRadius;   // nah = stark
            var inv = 1 / (d || 1);
            c.vx += dx * inv * CONFIG.fleeForce * push * dt;
            c.vy += dy * inv * CONFIG.fleeForce * push * dt;
            c.flee = 1;
          }
        }
        c.flee = Math.max(0, c.flee - CONFIG.calmDown * dt);

        // 3) Vom Rand wegdrehen
        if (c.x < CONFIG.margin)          c.vx += 0.07 * dt;
        else if (c.x > W - CONFIG.margin) c.vx -= 0.07 * dt;
        if (c.y < CONFIG.margin)          c.vy += 0.07 * dt;
        else if (c.y > H - CONFIG.margin) c.vy -= 0.07 * dt;

        // 4) Tempo aufs Ziel regeln: bei Flucht schnell, sonst zurück aufs
        //    Reisetempo. Die Richtung bleibt dabei unangetastet.
        var speed = Math.sqrt(c.vx * c.vx + c.vy * c.vy) || 0.0001;
        var target = c.base + (CONFIG.maxSpeed - c.base) * c.flee;
        var next = speed + (target - speed) * 0.05 * dt;
        c.vx = c.vx / speed * next;
        c.vy = c.vy / speed * next;

        // 5) Position. Wer es trotzdem rausschafft, kommt gegenüber wieder rein.
        c.x += c.vx * dt;
        c.y += c.vy * dt;
        if (c.x < -c.w) c.x = W + c.w;
        else if (c.x > W + c.w) c.x = -c.w;
        if (c.y < -c.h) c.y = H + c.h;
        else if (c.y > H + c.h) c.y = -c.h;

        c.el.style.transform =
            "translate3d(" + (c.x - c.w / 2).toFixed(1) + "px," +
            (c.y - c.h / 2).toFixed(1) + "px,0)";

        // 6) Blickrichtung der Fledermaus, nur bei Wechsel schreiben
        if (c.kind === "bat") {
          var face = c.vx < 0 ? -1 : 1;
          if (face !== c.face) {
            c.face = face;
            c.el.style.setProperty("--hw-face", face);
          }
        }

        // 7) Aufgeschreckt: schnellerer Flügelschlag bzw. hektischeres Wabern
        var fleeing = c.flee > 0.2;
        if (fleeing !== c.fleeing) {
          c.fleeing = fleeing;
          c.el.classList.toggle("is-fleeing", fleeing);
        }
      }

      requestAnimationFrame(step);
    }

    window.addEventListener("pointermove", function (e) {
      pointer.x = e.clientX;
      pointer.y = e.clientY;
      pointer.active = true;
    }, { passive: true });

    document.addEventListener("pointerleave", function () {
      pointer.active = false;
    });

    window.addEventListener("resize", function () {
      W = window.innerWidth;
      H = window.innerHeight;
    }, { passive: true });

    // Im Hintergrundtab nichts rechnen
    document.addEventListener("visibilitychange", function () {
      if (document.hidden) {
        running = false;
      } else if (!running) {
        running = true;
        last = 0;
        requestAnimationFrame(step);
      }
    });

    requestAnimationFrame(step);
  }

  function init(scene) {
    if (scene.dataset.hwReady) return;
    scene.dataset.hwReady = "1";

    // Parallaxe für Mond und Silhouette
    window.addEventListener("pointermove", function (e) {
      scene.style.setProperty("--hw-px", (e.clientX / window.innerWidth * 2 - 1).toFixed(3));
      scene.style.setProperty("--hw-py", (e.clientY / window.innerHeight * 2 - 1).toFixed(3));
    }, { passive: true });

    if (!reducedMotion()) create(scene);
  }

  function boot() {
    var scenes = document.querySelectorAll(".hw-scene");
    if (!scenes.length) return;

    // Hook für die Seite: solange eine Szene im Dokument liegt, kann das CSS
    // darüber Farben, Hintergründe oder Kontraste umstellen.
    document.body.classList.add("is-halloween");

    Array.prototype.forEach.call(scenes, init);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot);
  } else {
    boot();
  }
}